import React, { createContext, useState, useCallback, useContext } from 'react';
import ActionConfirm from '@shared/componets/feedback/ActionConfirm/ActionConfirm';

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
    const [confirmState, setConfirmState] = useState({
        visible: false,
        title: '',
        description: '',
        confirmText: 'Aceptar',
        cancelText: 'Cancelar',
        onConfirm: null,
        onCancel: null,
    });

    const confirm = useCallback(({ title, description, confirmText, cancelText }) => {
        return new Promise((resolve) => {
            const handleConfirm = () => {
                setConfirmState((prev) => ({ ...prev, visible: false }));
                setTimeout(() => resolve(true), 450);
            };

            const handleCancel = () => {
                setConfirmState((prev) => ({ ...prev, visible: false }));
                setTimeout(() => resolve(false), 450);
            };

            setConfirmState({
                visible: true,
                title,
                description,
                confirmText: confirmText || 'Aceptar',
                cancelText: cancelText || 'Cancelar',
                onConfirm: handleConfirm,
                onCancel: handleCancel,
            });
        });
    }, []);

    return (
        <ConfirmContext.Provider value={{ confirm }}>
            {children}
            <ActionConfirm {...confirmState} active={confirmState.visible} />
        </ConfirmContext.Provider>
    );
};




/**
 * Hook personalizado para disparar un cuadro de confirmación reutilizable.
 *
 * Este hook debe utilizarse dentro de un `<ConfirmProvider>` en el árbol de componentes.
 * Retorna una función `confirm` que puede llamarse desde cualquier componente para
 * mostrar un modal de confirmación y esperar la respuesta del usuario.
 * 
 * @returns {Function} confirm - Función asíncrona que muestra un modal y devuelve la decisión del usuario.
 *
 * @example
 * const confirm = useConfirm();
 * 
 * const handleDelete = async () => {
 *   const confirmed = await confirm({
 *     title: '¿Eliminar registro?',
 *     description: 'Esta acción no se puede deshacer.',
 *     confirmText: 'Sí, eliminar',     // Opcional - default: 'Aceptar'
 *     cancelText: 'No quiero',         // Opcional - default: 'Cancelar'
 *     onConfirm: () => console.log('Confirmado'), // Opcional
 *     onCancel: () => console.log('Cancelado'),   // Opcional
 *   });
 * 
 *   if (!confirmed) return;
 *   // continua con el codigo
 * };
 * 
 * return (
 *   <button onClick={handleDelete}>Eliminar</button>
 * );
 */

export const useConfirm = () => {
    const context = useContext(ConfirmContext);
    if (!context) throw new Error('useConfirm debe usarse dentro de ConfirmProvider');
    return context.confirm;
};


/*
Ejemplo de uso

const confirm = useConfirm();

const ejemploFuncion = () => {
    // Await es porque debe esperar a que el usuario ponga si o no
    const confirmed = await confirm({
        title: 'Eliminar el tipo seleccionado',
        description: 'Este cambio impactara inmediatamente y no se podra re'
    });
    if (!confirmed) return;
    // Aca ejeuta 
}
*/